import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useSettings } from '@/context/SettingsContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  Users,
  UserCircle,
  Package,
  ShoppingCart,
  Truck,
  CheckSquare,
  TrendingUp,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronRight,
  Search,
  Shield,
  Wallet,
  Award,
  Megaphone,
  History,
  ClipboardList,
  UserCheck,
  Calendar,
  Clock,
  Key,
  Link2,
  CreditCard,
  BookOpen,
  Building2,
  Store,
  Sparkles,
  FileText,
  Receipt,
  AlertTriangle,
  QrCode
} from 'lucide-react';
import { cn } from '@/lib/utils';
import ImpersonationBanner from '@/components/ImpersonationBanner';
import UsageLimitWall from '@/components/UsageLimitWall';
import { Button } from '@/components/ui/button';
import NotificationDropdown from '@/components/NotificationDropdown';
import GlobalSearch from '@/components/GlobalSearch';
import CompanySwitcher from '@/components/CompanySwitcher';
import useCompanyModules from '@/hooks/useCompanyModules';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const { loadCompanySettings } = useSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const [userPermissions, setUserPermissions] = useState([]);
  const [permissionsLoaded, setPermissionsLoaded] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [isShopifyConnected, setIsShopifyConnected] = useState(true); // Default true to avoid flash

  // Plan-driven module gating (single source of truth).
  // Returns `has(moduleKey)` that consults the tenant's plan + overrides.
  // Super-admins bypass gating. Network failures gracefully show all.
  const { has: hasModule, loaded: modulesLoaded } = useCompanyModules();
  
  // Load company settings when user changes
  useEffect(() => {
    if (user?.company_id) {
      loadCompanySettings(user.company_id);
    }
  }, [user?.company_id, loadCompanySettings]);
  
  // Check Shopify connection status (kept from legacy /auth/me flow)
  useEffect(() => {
    if (!user?.id) return;
    const token = localStorage.getItem('token');
    axios.get(`${API_BASE_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(r => {
      if (r.data?.is_shopify_connected === false) setIsShopifyConnected(false);
    }).catch(() => {});
  }, [user?.id]);
  
  // Start with sidebar closed on mobile (< 768px), open on desktop
  const [sidebarOpen, setSidebarOpen] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 768;
    }
    return true;
  });

  // Fetch user permissions on mount
  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user?.id) return;
      
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_BASE_URL}/users/${user.id}/permissions`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        const perms = response.data.permissions || [];
        // Admin gets all permissions
        if (response.data.is_admin) {
          setUserPermissions(['all']);
        } else {
          setUserPermissions(perms);
        }
      } catch (error) {
        console.error('Failed to fetch permissions:', error);
        // Fallback to empty permissions
        setUserPermissions([]);
      } finally {
        setPermissionsLoaded(true);
      }
    };

    fetchPermissions();
  }, [user?.id]);

  // Check if user has a specific permission
  const hasPermission = (permission) => {
    // No permission required → visible to everyone (e.g. self-service pages)
    if (!permission) return true;
    // Special case for super_admin permission
    if (permission === 'super_admin') {
      return user?.is_super_admin === true;
    }
    if (userPermissions.includes('all')) return true; // Admin
    return userPermissions.includes(permission);
  };

  // Navigation items with permission requirements
  // Each item requires a nav.* permission to be visible
  const navItems = [
    {
      label: 'Dashboard',
      icon: LayoutDashboard,
      path: '/dashboard',
      permission: 'nav.dashboard',
    },
    {
      label: 'My Deliveries',
      icon: ClipboardList,
      path: '/my-deliveries',
      permission: 'nav.my_deliveries',
    },
    {
      label: 'Orders',
      icon: ShoppingCart,
      path: '/orders',
      permission: 'nav.orders',
      children: [
        { label: 'All Orders', path: '/orders', permission: 'orders.view' },
        { label: 'Returns', path: '/orders/returns', permission: 'returns.view' },
        { label: 'Inwarding', path: '/orders/inwarding', permission: 'returns.inwarding.view' },
        { label: 'Missing Inventory', path: '/orders/missing-inventory', permission: 'returns.missing_inventory.view' },
        { label: 'Abandoned Checkouts', path: '/orders/abandoned', permission: 'abandoned_checkouts.view' },
        { label: 'Driver Performance', path: '/driver-performance', permission: 'orders.view' }
      ]
    },
    {
      label: 'Customers',
      icon: UserCircle,
      path: '/customers',
      permission: 'nav.orders',
      children: [
        { label: 'All Customers', path: '/customers', permission: 'nav.orders' },
        { label: 'Customer 360', path: '/customers/360', permission: 'nav.orders' },
        { label: 'Segments', path: '/customers/segments', permission: 'nav.orders' },
      ],
    },
    {
      label: 'Leads & Sales',
      icon: Users,
      path: '/leads',
      permission: 'nav.leads',
    },
    {
      label: 'Inventory',
      icon: Package,
      path: '/inventory',
      permission: 'nav.inventory',
      children: [
        { label: 'All Products', path: '/inventory', permission: 'inventory.view' },
        { label: 'Channel Listings', path: '/inventory/channel-listings', permission: 'inventory.view' },
        { label: 'Warehouses', path: '/inventory/warehouses', permission: 'inventory.warehouses.view' },
        { label: 'Packages', path: '/inventory/packages', permission: 'inventory.packages.view' },
        { label: 'Inwarding', path: '/inventory/inwarding', permission: 'inventory.inwarding' },
        { label: 'Settings', path: '/inventory/settings', permission: 'inventory.settings' }
      ]
    },
    {
      label: 'Vendors',
      icon: ShoppingCart,
      path: '/vendors',
      permission: 'nav.vendors',
      children: [
        { label: 'All Vendors', path: '/vendors', permission: 'nav.vendors' },
        { label: 'Marketplace Vendors', path: '/marketplace-admin', permission: 'nav.marketplace_admin' },
      ],
    },
    {
      label: 'Purchase Orders',
      icon: ShoppingCart,
      path: '/purchase-orders',
      permission: 'nav.purchase_orders',
    },
    {
      label: 'Deliveries',
      icon: Truck,
      path: '/deliveries',
      permission: 'nav.deliveries',
      children: [
        { label: 'All Deliveries', path: '/deliveries', permission: 'nav.deliveries' },
        { label: 'Track Sheet', path: '/delivery-track-sheet', permission: 'nav.deliveries' },
      ],
    },
    {
      label: 'Vendor Pickups',
      icon: Truck,
      path: '/vendor-pickups',
      permission: 'nav.deliveries',
    },
    {
      label: 'Marketplace Pickups',
      icon: QrCode,
      path: '/marketplace-pickups',
      // Show for any user with the driver action permission (custom
      // role drivers) — admins already get this via `all`. We don't
      // gate on `nav.marketplace_pickups` because admins routinely
      // forget to tick the nav-level permission when promoting a driver.
      permission: 'marketplace.pickups.driver',
    },
    {
      label: 'Tasks',
      icon: CheckSquare,
      path: '/tasks',
      permission: 'nav.tasks',
    },
    {
      label: 'Marketing',
      icon: TrendingUp,
      path: '/marketing',
      permission: 'nav.marketing',
      children: [
        { label: 'Inbox', path: '/marketing/inbox', permission: 'marketing.inbox.view' },
        { label: 'Inbox Settings', path: '/marketing/inbox/settings', permission: 'marketing.inbox.manage' },
        { label: 'AI Copilot', path: '/ai-copilot/settings', permission: 'marketing.inbox.manage' },
        { label: 'Broadcasts', path: '/marketing/broadcasts', permission: 'marketing.broadcasts.view' },
        { label: 'Templates', path: '/marketing/templates', permission: 'marketing.journeys.view' },
        { label: 'Journeys', path: '/marketing/journeys', permission: 'marketing.journeys.view' },
        { label: 'Push Notifications', path: '/marketing/push', permission: 'marketing.broadcasts.view' }
      ]
    },
    {
      label: 'Integrations',
      icon: Link2,
      path: '/integrations',
      permission: 'nav.integrations',
    },
    {
      label: 'POS',
      icon: Wallet,
      path: '/pos',
      permission: 'nav.pos',
      children: [
        { label: 'Open POS', path: '/pos', permission: 'nav.pos' },
        { label: 'POS Orders', path: '/pos/orders', permission: 'nav.pos' },
        { label: 'POS Setup', path: '/pos/setup', permission: 'nav.pos' },
      ]
    },
    {
      label: 'Analytics',
      icon: TrendingUp,
      path: '/analytics',
      permission: 'nav.dashboard',
    },
    {
      label: 'Cash Management',
      icon: Wallet,
      path: '/cash',
      permission: 'nav.cash',
    },
    {
      label: 'Expenses',
      icon: Receipt,
      path: '/expenses',
      permission: 'nav.expenses',
    },
    {
      label: 'My Expenses',
      icon: Receipt,
      path: '/my-expenses',
      // No permission required — every employee can submit their own
      // expenses. Hidden only when the Expenses module is disabled
      // at the tenant level (handled by useCompanyModules).
      module: 'expenses',
    },
    {
      label: 'Incentives',
      icon: Award,
      path: '/incentives',
      permission: 'nav.incentives',
    },
    {
      label: 'My Sales',
      icon: Award,
      path: '/my-sales',
      permission: 'incentives.performance_view_own',
    },
    {
      label: 'Sales Performance',
      icon: Award,
      path: '/sales-performance',
      permission: 'incentives.performance_view_all',
    },
    {
      label: 'Sales Targets',
      icon: Award,
      path: '/sales-targets',
      permission: 'incentives.targets_view',
    },
    {
      label: 'Delivery Incentives',
      icon: Award,
      path: '/delivery-incentives',
      permission: 'incentives.manage',
    },
    {
      label: 'Product Availability',
      icon: Package,
      path: '/product-availability',
      permission: 'nav.product_availability',
    },
    {
      label: 'Announcements',
      icon: Megaphone,
      path: '/announcements',
      permission: 'nav.announcements',
    },
    {
      label: 'User Management',
      icon: Users,
      path: '/users',
      permission: 'nav.users',
    },
    {
      label: 'Role Management',
      icon: Shield,
      path: '/roles',
      permission: 'nav.roles',
    },
    {
      label: 'Audit Log',
      icon: History,
      path: '/audit-log',
      permission: 'nav.settings',
    },
    {
      label: 'Settings',
      icon: Settings,
      path: '/settings',
      permission: 'nav.settings',
      children: [
        { label: 'General', path: '/settings', permission: 'nav.settings' },
        { label: 'Return Rules', path: '/settings/return-rules', permission: 'returns.rules.view' },
        { label: 'Delivery ETA', path: '/settings/delivery-eta', permission: 'nav.settings' },
        { label: 'Label Settings', path: '/settings/labels', permission: 'nav.settings' }
      ]
    },
    {
      label: 'Plans & Billing',
      icon: CreditCard,
      path: '/billing',
      permission: 'admin',
    },
    {
      label: 'API Keys',
      icon: Key,
      path: '/api-keys',
      permission: 'admin',
    },
    // Super Admin (only visible to super admins)
    {
      label: 'Super Admin',
      icon: Shield,
      path: '/super-admin',
      permission: 'super_admin',  // Special permission for super admins
    },
    // HR Module
    {
      label: 'HR Dashboard',
      icon: UserCheck,
      path: '/hr',
      permission: 'nav.hr',
    },
    {
      label: 'Mark Attendance',
      icon: Clock,
      path: '/hr/mark-attendance',
      permission: 'hr.mark_attendance',
    },
    {
      label: 'Leave Requests',
      icon: Calendar,
      path: '/hr/leaves',
      permission: 'hr.view_leaves',
    },
  ];

  // Profile is always visible (no permission required)
  const profileItem = {
    label: 'Profile',
    icon: Users,
    path: '/profile',
    permission: null, // Always visible
  };

  // Module name to nav item path mapping.
  // Keys MUST match slugs in backend `plans_module.MODULE_REGISTRY`.
  const moduleToNavPaths = {
    dashboard: ['/dashboard'],
    orders: ['/orders'],
    my_deliveries: ['/my-deliveries'],
    leads: ['/leads'],
    customers: ['/customers'],
    inventory: ['/inventory'],
    vendors: ['/vendors', '/marketplace-admin'],
    vendor_pickups: ['/vendor-pickups'],
    // NOTE: '/marketplace-pickups' is intentionally NOT listed here.
    // It was previously gated on a phantom `marketplace_pickups`
    // module key that doesn't exist in the backend MODULE_REGISTRY,
    // so `hasModule()` always returned false → the menu item was
    // hidden from EVERYONE (including admin) regardless of the
    // `marketplace.pickups.driver` permission. By leaving it unmapped
    // it falls through to `isModuleEnabled`'s `return true` default
    // and visibility is controlled purely by the permission check
    // (which admin satisfies via `all`).
    purchase_orders: ['/purchase-orders'],
    deliveries: ['/deliveries', '/delivery-track-sheet'],
    tasks: ['/tasks'],
    marketing: ['/marketing'],
    hr: ['/hr', '/hr/mark-attendance', '/hr/leaves'],
    cash: ['/cash'],
    expenses: ['/expenses', '/my-expenses'],
    incentives: ['/incentives'],
    announcements: ['/announcements'],
    api_keys: ['/api-keys'],
  };

  // Nav paths that are always shown regardless of plan modules (admin/infra).
  const ALWAYS_SHOW = ['/users', '/roles', '/settings', '/audit-log',
                       '/super-admin', '/profile', '/billing', '/integrations'];

  // Check if a nav item should be shown based on plan-level enabled modules.
  const isModuleEnabled = (navPath) => {
    if (ALWAYS_SHOW.some(p => navPath.startsWith(p))) return true;
    for (const [moduleKey, paths] of Object.entries(moduleToNavPaths)) {
      if (paths.some(p => navPath.startsWith(p))) {
        return hasModule(moduleKey);
      }
    }
    return true; // Unmapped paths default to visible
  };

  // Filter nav items based on user permissions AND enabled modules
  const filteredNavItems = permissionsLoaded 
    ? navItems.filter(item => hasPermission(item.permission) && isModuleEnabled(item.path))
    : [];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-wt-bg grain-overlay">
      <ImpersonationBanner />
      <UsageLimitWall />
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      <aside
        className={cn(
          'fixed left-0 top-0 z-40 h-screen border-r border-wt-border bg-wt-bg smooth-transition',
          // Desktop: always visible, collapsible width
          'md:translate-x-0',
          sidebarOpen ? 'w-64' : 'md:w-20 w-64',
          // Mobile: slide in/out
          sidebarOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'
        )}
        data-testid="sidebar"
      >
        <div className="flex h-16 items-center justify-between border-b border-wt-border px-4">
          <AnimatePresence mode="wait">
            {sidebarOpen && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="flex items-center"
              >
                <img 
                  src="/opswarex-logo.png" 
                  alt="Opswarex" 
                  className="h-12 w-auto"
                />
              </motion.div>
            )}
          </AnimatePresence>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="text-wt-text-secondary hover:text-wt-gold hidden md:flex"
            data-testid="sidebar-toggle"
          >
            {sidebarOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(false)}
            className="text-wt-text-secondary hover:text-wt-gold md:hidden"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        <nav className="h-[calc(100%-8rem)] overflow-y-auto px-4 py-4">
          <ul className="space-y-1">
            {!permissionsLoaded ? (
              // Loading skeleton
              <li className="px-3 py-2 text-wt-text-muted text-sm">Loading...</li>
            ) : filteredNavItems.length === 0 ? (
              // No permissions
              <li className="px-3 py-2 text-wt-text-muted text-sm">No menu access</li>
            ) : (
              filteredNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path || 
                  (item.children && item.children.some(c => location.pathname === c.path));
                const filteredChildren = item.children?.filter(child => hasPermission(child.permission)) || [];
                const hasChildren = filteredChildren.length > 0;
                
                return (
                  <li key={item.path}>
                    <button
                      onClick={() => {
                        if (!hasChildren) {
                          navigate(item.path);
                          // Close sidebar on mobile after navigation
                          if (window.innerWidth < 768) {
                            setSidebarOpen(false);
                          }
                        } else {
                          // For items with children, navigate to first permitted child
                          navigate(filteredChildren[0].path);
                          if (window.innerWidth < 768) {
                            setSidebarOpen(false);
                          }
                        }
                      }}
                      className={cn(
                        'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all',
                        isActive
                          ? 'bg-wt-gold/20 text-wt-gold'
                          : 'text-wt-text-secondary hover:bg-wt-subtle hover:text-wt-text-primary'
                      )}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Icon className="h-5 w-5 shrink-0" />
                      <AnimatePresence mode="wait">
                        {sidebarOpen && (
                          <motion.span
                            initial={{ opacity: 0, width: 0 }}
                            animate={{ opacity: 1, width: 'auto' }}
                            exit={{ opacity: 0, width: 0 }}
                            className="truncate"
                          >
                            {item.label}
                          </motion.span>
                        )}
                      </AnimatePresence>
                      {isActive && sidebarOpen && !hasChildren && (
                        <ChevronRight className="ml-auto h-4 w-4" />
                      )}
                    </button>
                    
                    {/* Sub-menu items */}
                    {hasChildren && sidebarOpen && isActive && (
                      <ul className="ml-6 mt-1 space-y-1 border-l border-wt-border/50 pl-3">
                        {filteredChildren.map((child) => {
                          const childActive = location.pathname === child.path;
                          return (
                            <li key={child.path}>
                              <button
                                onClick={() => {
                                  navigate(child.path);
                                  if (window.innerWidth < 768) {
                                    setSidebarOpen(false);
                                  }
                                }}
                                className={cn(
                                  'flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-xs transition-all',
                                  childActive
                                    ? 'text-wt-gold'
                                    : 'text-wt-text-muted hover:text-wt-text-primary'
                                )}
                              >
                                <span className={cn(
                                  "w-1.5 h-1.5 rounded-full",
                                  childActive ? "bg-wt-gold" : "bg-wt-text-muted/50"
                                )} />
                                {child.label}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    )}
                  </li>
                );
              })
            )}
            
            {/* Profile - always visible */}
            <li>
              <button
                onClick={() => {
                  navigate('/profile');
                  if (window.innerWidth < 768) {
                    setSidebarOpen(false);
                  }
                }}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all',
                  location.pathname === '/profile'
                    ? 'bg-wt-gold/20 text-wt-gold'
                    : 'text-wt-text-secondary hover:bg-wt-subtle hover:text-wt-text-primary'
                )}
                data-testid="nav-profile"
              >
                <Users className="h-5 w-5 shrink-0" />
                <AnimatePresence mode="wait">
                  {sidebarOpen && (
                    <motion.span
                      initial={{ opacity: 0, width: 0 }}
                      animate={{ opacity: 1, width: 'auto' }}
                      exit={{ opacity: 0, width: 0 }}
                      className="truncate"
                    >
                      Profile
                    </motion.span>
                  )}
                </AnimatePresence>
                {location.pathname === '/profile' && sidebarOpen && (
                  <ChevronRight className="ml-auto h-4 w-4" />
                )}
              </button>
            </li>
          </ul>
        </nav>

        {/* Logout Button */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-wt-border p-4">
          <button
            onClick={handleLogout}
            className="flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm text-wt-text-secondary hover:bg-wt-subtle hover:text-red-400 transition-all"
            data-testid="logout-button"
          >
            <LogOut className="h-5 w-5 shrink-0" />
            <AnimatePresence mode="wait">
              {sidebarOpen && (
                <motion.span
                  initial={{ opacity: 0, width: 0 }}
                  animate={{ opacity: 1, width: 'auto' }}
                  exit={{ opacity: 0, width: 0 }}
                >
                  Logout
                </motion.span>
              )}
            </AnimatePresence>
          </button>
        </div>
      </aside>

      {/* Main Content Area */}
      <div
        className={cn(
          'smooth-transition',
          sidebarOpen ? 'md:ml-64' : 'md:ml-20'
        )}
      >
        {/* Top Header */}
        <header className="sticky top-0 z-20 flex h-16 items-center justify-between border-b border-wt-border bg-wt-bg/80 backdrop-blur-lg px-4 md:px-6">
          {/* Mobile Menu Button */}
          <Button
            variant="ghost"
            size="icon"
            onClick={() => setSidebarOpen(true)}
            className="md:hidden text-wt-text-secondary hover:text-wt-gold"
            data-testid="mobile-menu-button"
          >
            <Menu className="h-5 w-5" />
          </Button>
          
          {/* Search Bar (Desktop Only) - Clickable to open command palette */}
          <div className="hidden md:flex flex-1 max-w-xl">
            <button
              onClick={() => setSearchOpen(true)}
              className="relative w-full text-left"
              data-testid="global-search-trigger"
            >
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-wt-text-muted" />
              <div className="w-full pl-10 pr-4 py-2 bg-wt-subtle border border-wt-border rounded-lg text-sm text-wt-text-muted cursor-pointer hover:border-wt-gold/50 transition-colors flex items-center justify-between">
                <span>Search...</span>
                <kbd className="hidden sm:inline-flex h-5 select-none items-center gap-1 rounded border border-wt-border bg-wt-bg px-1.5 font-mono text-[10px] font-medium text-wt-text-muted">
                  <span className="text-xs">⌘</span>K
                </kbd>
              </div>
            </button>
          </div>
          
          {/* Global Search Dialog */}
          <GlobalSearch open={searchOpen} setOpen={setSearchOpen} />
          
          {/* Right Side - Documents, NDR, Notifications & User */}
          <div className="flex items-center gap-4">
            {/* Documents Hub Icon */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/documents')}
              className="text-wt-text-muted hover:text-wt-text-primary hover:bg-wt-subtle relative"
              title="Documents Hub"
            >
              <FileText className="h-5 w-5" />
            </Button>
            
            {/* NDR Dashboard Icon */}
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/ndr')}
              className="text-wt-text-muted hover:text-wt-text-primary hover:bg-wt-subtle relative"
              title="NDR Dashboard"
            >
              <AlertTriangle className="h-5 w-5" />
            </Button>
            
            {/* Company Switcher (Super Admin only) */}
            <CompanySwitcher />
            
            <NotificationDropdown />
            
            <div className="flex items-center gap-3">
              <div className="text-right hidden sm:block">
                <p className="text-sm font-medium text-wt-text-primary">
                  {user?.full_name || user?.name}
                </p>
                <p className="text-xs text-wt-text-muted capitalize">
                  {user?.custom_role_name || user?.roles?.[0] || 'User'}
                </p>
              </div>
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-wt-gold to-wt-gold-hover flex items-center justify-center">
                <span className="text-sm font-bold text-wt-bg">
                  {(user?.full_name || user?.name || 'U').charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
